import {z} from 'zod';
import {SOCIAL_NETWORK_TYPES} from '../types/platforms';

export const PlatformSchema = z.object({}).passthrough();
export const IdentitySchema = z.object({
  provider: z.enum(SOCIAL_NETWORK_TYPES),
  cachedHandle: z.string().nullable(),
  cachedProfileImageUrl: z.string().nullable(),
  needsRelink: z.boolean(),
});
export const PortfolioMediaItemSchema = z.object({}).passthrough();

export const GenderSchema = z.string().nullable();

const SPECIALTIES = [
  'Animated GIF',
  'Art',
  'Beauty',
  'Bright',
  'Cars',
  'Celebrity',
  'Copywriting',
  'DIY',
  'Eco-Friendly',
  'Entertaining',
  'Entertainment',
  'Events',
  'Family',
  'Fashion',
  'Financial Planning',
  'Fitness',
  'Food',
  'Food and Beverage',
  'Graphic Designer',
  'Hair',
  'Healthy Living',
  'Home Decor',
  'Humor',
  'Illustrator',
  'Interior Design',
  'Lifestyle',
  'Men’s fashion',
  'Music',
  'On-Model',
  'Outdoors',
  'Parenting',
  'Party Planning',
  'Pets',
  'Pop Art',
  'Portraits',
  'Prop Styling',
  'Recipe Development',
  'Shopping',
  'Sports',
  'Stop-Motion Video',
  'Studio Photography',
  'Tech',
  'Travel',
  'Urban',
  'Weddings',
  "Women's fashion",
] as const;
export const SpecialtiesSchema = z.enum(SPECIALTIES);

const CAPABILITIES = [
  'professional photography',
  'illustration',
  'stop-motion video',
  'photo',
  'graphic design',
  'short-form video',
  'blog',
  'infographics',
  'copywriting',
  'blog writing',
  'photo/video editing',
  'animated gif',
  'long-form video',
  'video',
] as const;
export const CapabilitySchema = z.enum(CAPABILITIES);

const TagSchema = z.object({
  id: z.string(),
  name: SpecialtiesSchema,
});

const ContentPreferenceSchema = z.object({
  id: z.string(),
  kind: CapabilitySchema,
});

export const LocaleSchema = z.object({
  id: z.string(),
  apiUid: z.string(),
  city: z.string(),
  state: z.string().nullable(),
  country: z.string(),
  latitude: z.number(),
  longitude: z.number(),
});

export const ProfileProgressReportSchema = z.object({
  id: z.string(),
  points: z.number(),
  total: z.number(),
  profileDone: z.boolean(),
  portfolioDone: z.boolean(),
  socialNetworksDone: z.boolean(),
  paymentMethodDone: z.boolean(),
  shippingAddressDone: z.boolean(),
});

export const ProfileSchema = z.object({
  id: z.string(),
  bio: z.string().nullable(),
  birthDate: z.string().nullable(),
  displayName: z.string().optional(),
  gender: GenderSchema.optional(),
  influencer: z.boolean().nullable(),
  contentCreator: z.boolean().nullable(),
  tags: z.array(TagSchema),
  contentPreferences: z.array(ContentPreferenceSchema).nullable(),
  minCollaborationRate: z.number().nullable(),
  imageUrl: z.string().url().optional(),
  location: z.string().optional(),
  locale: LocaleSchema.optional(),
  portfolioMediaItems: z.array(PortfolioMediaItemSchema),
  portfolioLink: z.string().optional().nullable(),
  profileProgressReport: ProfileProgressReportSchema.optional().nullable(),
});

export const UserSchema = z.object({
  id: z.string(),
  platforms: z.array(PlatformSchema).optional(),
  birthDate: z.coerce.date().nullable(),
  email: z.string().email(),
  identities: z.array(IdentitySchema).optional(),
  profile: ProfileSchema.optional(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export const ConnectedPlatformsStateSchema = z.object({
  user: UserSchema.optional(),
  avatarUrl: z.string().url().optional(),
  connectedPlatforms: z.array(PlatformSchema),
  distinctConnectedPlatforms: z.array(PlatformSchema),
  brandName: z.string().optional(),
});

export type User = z.infer<typeof UserSchema>;
export type Profile = z.infer<typeof ProfileSchema>;
export type Specialty = z.infer<typeof SpecialtiesSchema>;
export type Capability = z.infer<typeof CapabilitySchema>;
export type Locale = z.infer<typeof LocaleSchema>;
export type Gender = z.infer<typeof GenderSchema>;
